import { EventEmitter } from 'eventemitter3'
import { v4 as uuidv4 } from 'uuid'

// 定义所有事件类型
export interface EventTypes {
  // 弹窗相关事件
  'modal.opened': { modalId: string; type: string; payload: any; timestamp: number }
  'modal.confirmed': { modalId: string; data: any; timestamp: number }
  'modal.cancelled': { modalId: string; reason?: string; timestamp: number }
  'modal.pending': { modalId: string; pending: boolean; timestamp: number }
  'modal.closed': { modalId: string; result?: any; timestamp: number }
  'modal.state_changed': { modalId: string; state: string; context: any; timestamp: number }

  // 业务操作事件
  'order.update.requested': { orderId: string; data: any; userId: string; timestamp: number }
  'order.update.started': { orderId: string; operationId: string; timestamp: number }
  'order.update.success': { orderId: string; operationId: string; result: any; timestamp: number }
  'order.update.failed': { orderId: string; operationId: string; error: any; timestamp: number }

  // 系统事件
  'system.error': { error: Error; context: string; timestamp: number }
  'system.performance': { operation: string; duration: number; timestamp: number }
  'analytics.track': { event: string; properties: Record<string, any>; timestamp: number }
}

// 事件中间件接口
export interface EventMiddleware {
  name: string
  before?: <K extends keyof EventTypes>(event: K, data: EventTypes[K]) => boolean | Promise<boolean>
  after?: <K extends keyof EventTypes>(event: K, data: EventTypes[K]) => void | Promise<void>
  error?: (error: Error, event: string, data: any) => void
}

// 事件总线配置
export interface EventBusConfig {
  debug?: boolean
  maxListeners?: number
  enableAnalytics?: boolean
  enablePerformanceTracking?: boolean
}

class EnterpriseEventBus {
  private emitter: EventEmitter
  private middlewares: EventMiddleware[] = []
  private config: EventBusConfig
  private operationTracker = new Map<string, { start: number; operation: string }>()

  constructor(config: EventBusConfig = {}) {
    this.emitter = new EventEmitter()
    this.config = {
      debug: false,
      maxListeners: 100,
      enableAnalytics: true,
      enablePerformanceTracking: true,
      ...config,
    }

    // EventEmitter3 doesn't have setMaxListeners, but has maxListeners property
    if (this.config.maxListeners) {
      ;(this.emitter as any).maxListeners = this.config.maxListeners
    }
    this.setupDefaultMiddlewares()
  }

  // 添加中间件
  use(middleware: EventMiddleware): void {
    this.middlewares.push(middleware)
    if (this.config.debug) {
      console.log(`[EventBus] Middleware registered: ${middleware.name}`)
    }
  }

  // 类型安全的事件发射
  async emit<K extends keyof EventTypes>(event: K, data: EventTypes[K]): Promise<boolean> {
    try {
      // 执行前置中间件
      for (const middleware of this.middlewares) {
        if (middleware.before) {
          const shouldContinue = await middleware.before(event, data)
          if (!shouldContinue) {
            if (this.config.debug) {
              console.log(
                `[EventBus] Event ${String(event)} blocked by middleware: ${middleware.name}`,
              )
            }
            return false
          }
        }
      }

      // 发射事件
      const result = this.emitter.emit(event as string, data)

      // 执行后置中间件
      for (const middleware of this.middlewares) {
        if (middleware.after) {
          await middleware.after(event, data)
        }
      }

      return result
    } catch (error) {
      this.handleError(error as Error, String(event), data)
      return false
    }
  }

  // 类型安全的事件监听
  on<K extends keyof EventTypes>(
    event: K,
    listener: (data: EventTypes[K]) => void | Promise<void>,
  ): this {
    this.emitter.on(event as string, listener)
    return this
  }

  // 一次性监听
  once<K extends keyof EventTypes>(
    event: K,
    listener: (data: EventTypes[K]) => void | Promise<void>,
  ): this {
    this.emitter.once(event as string, listener)
    return this
  }

  // 移除监听器
  off<K extends keyof EventTypes>(
    event: K,
    listener?: (data: EventTypes[K]) => void | Promise<void>,
  ): this {
    if (listener) {
      this.emitter.off(event as string, listener)
    } else {
      this.emitter.removeAllListeners(event as string)
    }
    return this
  }

  // 开始性能追踪
  startOperation(operation: string): string {
    const operationId = uuidv4()
    this.operationTracker.set(operationId, {
      start: performance.now(),
      operation,
    })
    return operationId
  }

  // 结束性能追踪
  endOperation(operationId: string): void {
    const tracker = this.operationTracker.get(operationId)
    if (tracker && this.config.enablePerformanceTracking) {
      const duration = performance.now() - tracker.start
      this.emit('system.performance', {
        operation: tracker.operation,
        duration,
        timestamp: Date.now(),
      })
      this.operationTracker.delete(operationId)
    }
  }

  // 错误处理
  private handleError(error: Error, event: string, data: any): void {
    // 通知中间件
    this.middlewares.forEach((middleware) => {
      middleware.error?.(error, event, data)
    })

    // 发射系统错误事件
    this.emitter.emit('system.error', {
      error,
      context: `Event: ${event}`,
      timestamp: Date.now(),
    })

    if (this.config.debug) {
      console.error(`[EventBus] Error in event ${event}:`, error)
    }
  }

  // 设置默认中间件
  private setupDefaultMiddlewares(): void {
    // 调试中间件
    if (this.config.debug) {
      this.use({
        name: 'debug',
        before: (event, data) => {
          console.log(`[EventBus] Emitting: ${String(event)}`, data)
          return true
        },
      })
    }

    // 分析中间件
    if (this.config.enableAnalytics) {
      this.use({
        name: 'analytics',
        after: (event, data) => {
          if (event.startsWith('modal.') || event.startsWith('order.')) {
            this.emit('analytics.track', {
              event: String(event),
              properties: { ...data },
              timestamp: Date.now(),
            })
          }
        },
      })
    }
  }

  // 获取事件统计
  getStats(): {
    listenerCount: Record<string, number>
    totalEvents: number
    activeOperations: number
  } {
    const events = this.emitter.eventNames()
    const listenerCount: Record<string, number> = {}

    events.forEach((event) => {
      listenerCount[String(event)] = this.emitter.listenerCount(event)
    })

    return {
      listenerCount,
      totalEvents: events.length,
      activeOperations: this.operationTracker.size,
    }
  }

  // 清理资源
  destroy(): void {
    this.emitter.removeAllListeners()
    this.operationTracker.clear()
    this.middlewares.length = 0
  }
}

// 创建全局事件总线实例
export const eventBus = new EnterpriseEventBus({
  debug: import.meta.env.DEV,
  enableAnalytics: true,
  enablePerformanceTracking: true,
})

// 导出类
export { EnterpriseEventBus }
